/*
  A squad's chemistry and meta rating: the squad builder's own maths, so a
  figure shown outside the builder matches the one inside it.

  Ported from web's `functions/calculateChemistry.ts` and
  `redux/squadBuilder/squadBuilderUtils.ts` so the app can use it. Web still
  has its own copy; the two should be folded into this one.
*/
export const HERO_CLUB_IDS = [114605, 132794]; // hero, Hall of FUT
export const ICON_CLUB_ID = 112658;
export const formationsById = (formations = []) => Object.fromEntries(formations.map((f) => [f.id, f]));
const countryCountToChem = (count = 0) => count < 2 ? 0 : count < 5 ? 1 : count < 8 ? 2 : 3;
const leagueCountToChem = (count = 0) => count < 3 ? 0 : count < 5 ? 1 : count < 8 ? 2 : 3;
const clubCountToChem = (count = 0) => count < 2 ? 0 : count < 4 ? 1 : count < 7 ? 2 : 3;
/** Linked clubs (a men's and women's side, say) count as one for chemistry. */
export const buildClubLinks = (clubs = []) => {
    const links = {};
    for (const club of clubs) {
        const linked = club.linkedClubIds?.[0];
        if (linked == null)
            continue;
        const canonical = Math.min(club.id, linked);
        links[club.id] = canonical;
        links[linked] = canonical;
    }
    return links;
};
/** Squad meta rating weight per player role id; roles without one count as 1. */
export const roleWeightsByRoleId = (playerRoles) => Object.fromEntries(playerRoles.map((pr) => [pr.id, pr.squadMetaRatingWeight ?? 1]));
/** Each starter's chemistry (0–3), by slot index. Empty slots are skipped. */
export const calculateSquadChemistry = ({ players, formationPositions, manager, chemistryProfiles, clubLinks, }) => {
    const inPosition = players
        .slice(0, 11)
        .map((player, index) => ({ player, index }))
        .filter((s) => !!s.player?.possiblePositions?.length);
    const isInPosition = ({ player, index }) => player.possiblePositions.includes(formationPositions[index] ?? "");
    const isUniversal = (p) => p.clubId === ICON_CLUB_ID ||
        chemistryProfiles.leagueBonusUniversal.includes(p.versionId);
    const clubOf = (p) => clubLinks?.[p.clubId] ?? p.clubId;
    const countries = {};
    const leagues = {};
    const clubs = {};
    const add = (counter, key, n) => {
        counter[key] = (counter[key] ?? 0) + n;
    };
    let icons = 0;
    for (const slot of inPosition) {
        if (!isInPosition(slot))
            continue;
        const p = slot.player;
        if (isUniversal(p))
            icons += 1;
        add(countries, p.countryId, chemistryProfiles.countryBonuses[p.versionId] || 1);
        add(leagues, p.leagueId, isUniversal(p) ? 0 : chemistryProfiles.leagueBonuses[p.versionId] || 1);
        add(clubs, clubOf(p), HERO_CLUB_IDS.includes(p.clubId) || p.clubId === ICON_CLUB_ID
            ? 0
            : chemistryProfiles.clubBonuses[p.versionId] || 1);
    }
    if (manager?.country)
        add(countries, manager.country, 1);
    if (manager?.league)
        add(leagues, manager.league, 1);
    for (const key of Object.keys(leagues))
        add(leagues, Number(key), icons);
    const result = {};
    for (const slot of inPosition) {
        const p = slot.player;
        if (!isInPosition(slot)) {
            result[slot.index] = 0;
        }
        else if (p.clubId === ICON_CLUB_ID ||
            HERO_CLUB_IDS.includes(p.clubId) ||
            chemistryProfiles.playerFullChem.includes(p.versionId)) {
            result[slot.index] = 3;
        }
        else {
            const chem = countryCountToChem(countries[p.countryId]) +
                leagueCountToChem(leagues[p.leagueId]) +
                clubCountToChem(clubs[clubOf(p)]);
            result[slot.index] = Math.min(chem, 3);
        }
    }
    return result;
};
/**
 * The squad's chemistry (0–33) and its role-weighted meta rating. An empty
 * slot adds 0 to the rating and its role's weight to the divisor, as in the
 * squad builder.
 */
export const calculateSquadMetaRatingAndChemistry = ({ players, playerRoleIds, formationPositions, manager, chemistryProfiles, clubLinks, roleWeights, }) => {
    const playersChemistry = calculateSquadChemistry({
        players,
        formationPositions,
        manager,
        chemistryProfiles,
        clubLinks,
    });
    const chemistry = Object.values(playersChemistry).reduce((sum, c) => sum + c, 0);
    let ratingSum = 0;
    let weightSum = 0;
    for (let i = 0; i < 11; i++) {
        const roleId = playerRoleIds[i];
        const weight = (roleId != null ? roleWeights[roleId] : undefined) ?? 1;
        const rating = players[i]?.metaRatings?.find((m) => (roleId === undefined || m.playerRoleId === roleId) &&
            m.chemistry === (playersChemistry[i] ?? 0))?.metaRating ?? 0;
        ratingSum += rating * weight;
        weightSum += weight;
    }
    return {
        metaRating: weightSum > 0 ? ratingSum / weightSum : 0,
        chemistry,
        playersChemistry,
    };
};
//# sourceMappingURL=squadMetaRating.js.map